/**
 * LocaisInfoModal - Modal informativo para formato de Excel de Locais de Contagem
 *
 * Mostra informações sobre:
 * - Colunas do Excel e seus campos correspondentes
 * - Campo obrigatório (apenas 'local')
 * - Comportamento de sobrescrita por nome do local
 */
import React from 'react';
import { Info, X, AlertTriangle } from 'lucide-react';

interface LocaisInfoModalProps {
  onClose: () => void;
}

export const LocaisInfoModal: React.FC<LocaisInfoModalProps> = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-6 py-4 rounded-t-xl flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Info className="text-white" size={24} />
            <h3 className="text-lg font-semibold text-white">
              Formato do Excel - Locais de Contagem
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-1 transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Warning about overwrite */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-semibold text-amber-800 dark:text-amber-200">
                  Comportamento de Sobrescrita
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  Se um local com o <strong>mesmo nome</strong> já existir neste hospital,
                  ele será <strong>SOBRESCRITO</strong> com os novos dados do Excel.
                </p>
              </div>
            </div>
          </div>

          {/* Columns info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
              📋 Colunas do Excel
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
              O arquivo Excel deve conter as seguintes colunas no cabeçalho (linha 1):
            </p>

            <div className="grid grid-cols-2 gap-4">
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2 list-inside">
                <li><strong>A: local</strong> <span className="text-red-500 font-bold">*</span> (Nome do local)</li>
                <li><strong>B: responsavel</strong></li>
                <li><strong>C: telefone</strong></li>
              </ul>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2 list-inside">
                <li><strong>D: ramal</strong></li>
                <li><strong>E: email</strong></li>
                <li><strong>F: ativo</strong> (S/N)</li>
              </ul>
            </div>
          </div>

          {/* Required field note */}
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-3">
            <p className="text-sm text-red-700 dark:text-red-300">
              <span className="text-red-500 font-bold">*</span> <strong>Campo obrigatório:</strong> Apenas o nome do local (coluna A) é obrigatório. Os demais campos são opcionais.
            </p>
          </div>

          <div className="text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-4">
            <p>💡 <strong>Dica:</strong> Baixe o template clicando em "Baixar Template" na tela de importação.</p>
          </div>

          {/* Footer */}
          <div className="flex justify-end pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg transition"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
